/**
 * Core type definitions for DAFNE Log
 */

// Import centralized DAFNE configuration
import {
  BG_THRESHOLDS,
  KETONE_THRESHOLDS,
  KETONE_BG_COMBINED,
  KETONE_RECHECK_MINUTES,
  IOB_CONFIG,
  EXERCISE_ADJUSTMENTS,
  calculateIOB as configCalculateIOB,
  DEFAULT_SETTINGS as CONFIG_DEFAULT_SETTINGS,
  PERIOD_TAG_INFO as CONFIG_PERIOD_TAG_INFO,
} from "../config/dafne";

// Re-export config for convenience
export { BG_THRESHOLDS, KETONE_THRESHOLDS, IOB_CONFIG } from "../config/dafne";

/**
 * Helper to get timestamp as number (handles Date objects, ISO strings, and numbers).
 * Use this when working with entries that may have been loaded from the server,
 * since JSON serialization converts Date objects to ISO strings.
 */
export function getTimestampMs(t: Date | string | number | unknown): number {
  if (t instanceof Date) return t.getTime();
  if (typeof t === "string") return new Date(t).getTime();
  if (typeof t === "number") return t;
  return new Date(t as string).getTime();
}

// DAFNE field labels - used consistently across the app
export const DAFNE_LABELS = {
  TIME: "Time",
  GL: "GL", // Glucose Level (mmol/L)
  CP: "CP", // Carb Portions (grams)
  QUICK: "Quick", // Quick-acting insulin
  LONG: "Long", // Long-acting/background insulin
  KET: "Ket", // Ketones
  NOTE: "Note",
  EXERCISE: "Exercise",
} as const;

// Array of main diary row labels (in display order)
export const DIARY_ROW_LABELS = [
  DAFNE_LABELS.TIME,
  DAFNE_LABELS.GL,
  DAFNE_LABELS.CP,
  DAFNE_LABELS.QUICK,
  DAFNE_LABELS.LONG,
] as const;

// Blood glucose units
export type BgUnit = "mmol" | "mgdl";

// Carbohydrate units
export type CarbUnit = "grams" | "cp10";

// Period tags - user selects which period an entry belongs to
// Main markers: 3AM, BF, L, D, BB (fixed columns in DAFNE diary)
// Other: entries logged between markers (displayed in time order)
// Legacy after-* tags mapped to Other for backward compatibility
export type PeriodTag =
  | "3AM" // 3am check (overnight fasting)
  | "B" // Breakfast (BF)
  | "L" // Lunch
  | "EM" // Dinner (D) - was Evening Meal
  | "BT" // Before Bed (BB)
  | "Other" // Any entry between markers
  // Legacy - treat as Other in display
  | "after-B"
  | "after-L"
  | "after-EM";

// The 5 DAFNE diary marker columns (in order)
export const DAFNE_MARKERS = ["3AM", "B", "L", "EM", "BT"] as const;
export type DafneMarker = (typeof DAFNE_MARKERS)[number];

// Check if a period is a main marker (not Other or legacy after-*)
export function isMarkerPeriod(period: PeriodTag): period is DafneMarker {
  return DAFNE_MARKERS.includes(period as DafneMarker);
}

// Exercise types
export type ExerciseType =
  | "walk"
  | "run"
  | "cycle"
  | "swim"
  | "gym"
  | "sports"
  | "other";
export type ExerciseIntensity = "light" | "moderate" | "vigorous";

export const EXERCISE_TYPE_INFO: Record<
  ExerciseType,
  { label: string; icon: string }
> = {
  walk: { label: "Walk", icon: "person-walking" },
  run: { label: "Run", icon: "lightning" },
  cycle: { label: "Cycle", icon: "bicycle" },
  swim: { label: "Swim", icon: "water" },
  gym: { label: "Gym", icon: "trophy" },
  sports: { label: "Sports", icon: "dribbble" },
  other: { label: "Other", icon: "activity" },
};

export const EXERCISE_INTENSITY_INFO: Record<
  ExerciseIntensity,
  { label: string; bgEffect: string }
> = {
  light: { label: "Light", bgEffect: "Slight drop" },
  moderate: { label: "Moderate", bgEffect: "Moderate drop" },
  vigorous: { label: "Vigorous", bgEffect: "Significant drop" },
};

// Exercise entry (embedded in LogEntry)
export interface ExerciseData {
  type: ExerciseType;
  intensity: ExerciseIntensity;
  duration: number; // minutes
}

// Sick day tracking (DAFNE sick day rules)
export type SickStatus = "sick" | "better";

export const SICK_STATUS_INFO: Record<
  SickStatus,
  { label: string; icon: string; color: string }
> = {
  sick: { label: "I'm Sick", icon: "thermometer-half", color: "#dc3545" },
  better: { label: "I'm Better", icon: "emoji-smile", color: "#198754" },
};

// Sick day entry data
export interface SickDayData {
  status: SickStatus;
  symptoms?: string; // Optional symptoms description
}

/**
 * Entry source - tracks where a log entry originated from.
 * Used for deduplication on reimport and to mark entries as readonly.
 */
export type EntrySource = "manual" | "cgm-libreview" | "cgm-dexcom";

/**
 * Entry context describes how close entry time is to current logging time.
 * Used to distinguish live entry from delayed/backdated history logging.
 */
export type EntryContext = "live" | "delayed" | "backdated" | "future";

// Log entry - the core data record
export interface LogEntry {
  id: string;
  timestamp: Date; // Actual time (always required)
  entryContext?: EntryContext; // Live vs delayed/backdated/future context
  loggedAt?: Date; // When user logged the entry in the app
  minutesLag?: number; // LoggedAt - timestamp (positive means logged later)
  periodTag: PeriodTag; // User-selected period for ratio lookup
  bloodGlucose?: number; // GL - Always stored in mmol/L
  carbs?: number; // CP - Always stored in grams
  qaBase?: number; // Quick base dose (for carbs) - Units
  qaCorrection?: number; // Quick correction (+/-) - Units
  biBase?: number; // Long base dose - Units
  biCorrection?: number; // Long correction (+/-) - Units
  ketones?: number; // Ket - mmol/L
  exercise?: ExerciseData; // Exercise entry
  sickDay?: SickDayData; // Sick day status
  notes?: string; // Note
  // Settings snapshot - capture ratios used at entry time for historical review
  cpRatioUsed?: number; // Units per CP ratio used when entry was created
  correctionFactorUsed?: number; // ISF (mmol/L per unit) used when entry was created
  createdAt: Date;
  updatedAt: Date;
  // Source tracking - for CGM import deduplication
  /** Where this entry came from - manual input or CGM import */
  source?: EntrySource;
  /** Unique identifier from CGM source for deduplication on reimport */
  sourceEventId?: string;
  /** Locked entries from CGM imports cannot be edited to prevent conflicts */
  locked?: boolean;
}

// Day note types - for personal and clinic notes attached to specific days
export type DayNoteType = "personal" | "clinic";

// Day note - notes attached to a specific day's DAFNE log
export interface DayNote {
  id: string;
  date: string; // YYYY-MM-DD format - the day this note belongs to
  type: DayNoteType;
  content: string; // The note text
  authorId: string; // User who created the note
  authorName?: string; // Display name of author (mainly for clinic notes)
  createdAt: Date;
  updatedAt: Date;
}

// Calculated total insulin from an entry
export function getQATotal(entry: LogEntry): number | undefined {
  if (entry.qaBase === undefined && entry.qaCorrection === undefined)
    return undefined;
  return (entry.qaBase || 0) + (entry.qaCorrection || 0);
}

export function getBITotal(entry: LogEntry): number | undefined {
  if (entry.biBase === undefined && entry.biCorrection === undefined)
    return undefined;
  return (entry.biBase || 0) + (entry.biCorrection || 0);
}

/**
 * Check if entry's ratios differ from current settings.
 * Returns object indicating which ratios changed for historical review indicators.
 */
export interface RatioChangeInfo {
  cpRatioChanged: boolean;
  correctionFactorChanged: boolean;
  oldCpRatio?: number;
  newCpRatio?: number | null;
  oldCorrectionFactor?: number;
  newCorrectionFactor?: number | null;
}

export function getEntryRatioChanges(
  entry: LogEntry,
  currentSettings: {
    cpRatios: Record<string, number | null>;
    correctionFactor: number | null;
  },
  periodTagInfo: Record<string, { ratioFrom: string }>,
): RatioChangeInfo {
  const ratioKey = periodTagInfo[entry.periodTag]?.ratioFrom || entry.periodTag;
  const currentCpRatio = currentSettings.cpRatios[ratioKey];
  const currentCorrectionFactor = currentSettings.correctionFactor;

  return {
    cpRatioChanged:
      entry.cpRatioUsed !== undefined &&
      currentCpRatio !== null &&
      entry.cpRatioUsed !== currentCpRatio,
    correctionFactorChanged:
      entry.correctionFactorUsed !== undefined &&
      currentCorrectionFactor !== null &&
      entry.correctionFactorUsed !== currentCorrectionFactor,
    oldCpRatio: entry.cpRatioUsed,
    newCpRatio: currentCpRatio,
    oldCorrectionFactor: entry.correctionFactorUsed,
    newCorrectionFactor: currentCorrectionFactor,
  };
}

// Format insulin display as "base +/- correction" or just total (rounded to 1dp)
export function formatInsulinDose(base?: number, correction?: number): string {
  if (base === undefined && correction === undefined) return "";
  const roundedBase =
    base !== undefined ? Math.round(base * 10) / 10 : undefined;
  const roundedCorr =
    correction !== undefined ? Math.round(correction * 10) / 10 : undefined;
  if (roundedCorr === undefined || roundedCorr === 0)
    return `${roundedBase || 0}`;
  if (roundedBase === undefined || roundedBase === 0)
    return roundedCorr > 0 ? `+${roundedCorr}` : `${roundedCorr}`;
  const sign = roundedCorr > 0 ? "+" : "";
  return `${roundedBase} ${sign}${roundedCorr}`;
}

// Insulin delivery method
export type InsulinDelivery = "mdi" | "pump";

// Minimum dose increment (depends on delivery method/device)
export type DoseIncrement = 1 | 0.5 | 0.1 | 0.05;

export const DOSE_INCREMENT_INFO: Record<
  DoseIncrement,
  { label: string; description: string }
> = {
  1: { label: "1 unit", description: "Standard syringes" },
  0.5: {
    label: "0.5 units",
    description: "Half-unit pens (e.g., NovoPen Echo)",
  },
  0.1: { label: "0.1 units", description: "Insulin pumps" },
  0.05: { label: "0.05 units", description: "Advanced pumps" },
};

// Quick-acting insulin types
export type QAInsulinType =
  | "novorapid"
  | "humalog"
  | "fiasp"
  | "apidra"
  | "actrapid"
  | "other";

// Background insulin types (N/A for pump users)
export type BIInsulinType =
  | "lantus"
  | "levemir"
  | "tresiba"
  | "toujeo"
  | "protaphane"
  | "humulin-nph"
  | "other"
  | "none";

// BI frequency (for twice-daily insulins like NPH, Levemir)
export type BIFrequency = "once" | "twice";

// Correction stacking mode - how IOB affects correction suggestions
// no-stacking: Don't suggest corrections when IOB > 0 (DAFNE recommended)
// iob-adjusted: Subtract IOB from raw correction (current behavior)
export type CorrectionStackingMode = "no-stacking" | "iob-adjusted";

export const CORRECTION_STACKING_INFO: Record<
  CorrectionStackingMode,
  { label: string; description: string }
> = {
  "no-stacking": {
    label: "No Stacking (DAFNE)",
    description:
      "Don't suggest corrections while insulin is still working. Always dose for carbs.",
  },
  "iob-adjusted": {
    label: "IOB-Adjusted",
    description:
      "Subtract active insulin from correction. May suggest smaller corrections while IOB active.",
  },
};

// Insulin type display names
export const QA_INSULIN_NAMES: Record<QAInsulinType, string> = {
  novorapid: "NovoRapid (Aspart)",
  humalog: "Humalog (Lispro)",
  fiasp: "Fiasp",
  apidra: "Apidra (Glulisine)",
  actrapid: "Actrapid",
  other: "Other",
};

export const BI_INSULIN_NAMES: Record<BIInsulinType, string> = {
  lantus: "Lantus (Glargine)",
  levemir: "Levemir (Detemir)",
  tresiba: "Tresiba (Degludec)",
  toujeo: "Toujeo",
  protaphane: "Protaphane (NPH)",
  "humulin-nph": "Humulin NPH",
  other: "Other",
  none: "N/A (Pump)",
};

// Period definition (for ratio management and display)
export interface Period {
  id: string; // e.g., 'B', 'L', 'EM', 'BT', '3AM'
  name: string; // e.g., "Breakfast", "Lunch"
  shortName: string; // e.g., "B", "L"
  sortOrder: number; // Display order
  defaultStartHour: number; // For smart period suggestion
  defaultEndHour: number; // For smart period suggestion
}

// User settings (singleton record)
// Typical meal/event times for smart period detection
export interface MealTimes {
  wakeUp: string; // HH:MM format, e.g., "06:30"
  breakfast: string; // e.g., "07:00"
  lunch: string; // e.g., "12:00"
  dinner: string; // e.g., "18:30"
  bedtime: string; // e.g., "22:00"
}

export const DEFAULT_MEAL_TIMES: MealTimes = {
  wakeUp: "06:30",
  breakfast: "07:00",
  lunch: "12:00",
  dinner: "18:30",
  bedtime: "22:00",
};

export interface UserSettings {
  id: "settings"; // Always "settings"
  /** Optional preferred name shown on the Dashboard greeting. Falls back to the
   *  account display name when empty. */
  nickname?: string;
  bgUnits: BgUnit;
  carbUnits: CarbUnit;
  targetBgLow: number; // mmol/L
  targetBgHigh: number; // mmol/L
  cpRatios: Record<string, number | null>; // periodId -> units per CP (null = not set)
  correctionFactor: number | null; // mmol/L per unit (null = not set)
  correctionStacking?: CorrectionStackingMode; // How IOB affects corrections (default: no-stacking)
  // Insulin configuration
  insulinDelivery: InsulinDelivery;
  doseIncrement: DoseIncrement; // Minimum dose step
  qaInsulinType: QAInsulinType;
  qaInsulinCustomName?: string; // If type is 'other'
  biInsulinType: BIInsulinType;
  biInsulinCustomName?: string; // If type is 'other'
  biFrequency: BIFrequency; // Once or twice daily
  // Meal timing patterns for smart period detection
  mealTimes?: MealTimes;
  // Target profile (standard or upper targets)
  targetProfile?: TargetProfileType;
  // Data sharing consent for training and support
  dataSharing?: DataSharingConsent;
}

// Target profile type - imported from config but re-exported here for convenience
export type TargetProfileType = "standard" | "upper";

// Data sharing consent options
export type DataSharingLevel = "none" | "anonymous" | "identified";

export interface DataSharingConsent {
  /** Allow clinic to share DAFNE log data for training/support */
  allowDafneDataSharing: DataSharingLevel;
  /** Allow clinic to share CGM data for training/support */
  allowCgmDataSharing: DataSharingLevel;
  /** Timestamp when consent was last updated */
  consentUpdatedAt?: Date;
}

export const DATA_SHARING_LABELS: Record<
  DataSharingLevel,
  { label: string; description: string }
> = {
  none: {
    label: "Do not share",
    description: "Your data will only be visible to your approved clinicians",
  },
  anonymous: {
    label: "Share anonymously",
    description:
      "Data may be used for training/support but will not be tied back to you",
  },
  identified: {
    label: "Share with identity",
    description: "Data may be shared with your identity for case discussions",
  },
};

// Default periods (DAFNE standard)
export const DEFAULT_PERIODS: Period[] = [
  {
    id: "B",
    name: "Breakfast",
    shortName: "B",
    sortOrder: 1,
    defaultStartHour: 5,
    defaultEndHour: 10,
  },
  {
    id: "L",
    name: "Lunch",
    shortName: "L",
    sortOrder: 2,
    defaultStartHour: 10,
    defaultEndHour: 15,
  },
  {
    id: "EM",
    name: "Evening Meal",
    shortName: "EM",
    sortOrder: 3,
    defaultStartHour: 15,
    defaultEndHour: 21,
  },
  {
    id: "BT",
    name: "Bedtime",
    shortName: "BT",
    sortOrder: 4,
    defaultStartHour: 21,
    defaultEndHour: 24,
  },
  {
    id: "3AM",
    name: "Night",
    shortName: "3AM",
    sortOrder: 5,
    defaultStartHour: 0,
    defaultEndHour: 5,
  },
];

// Period tag display info - imported from centralized config
export const PERIOD_TAG_INFO: Record<
  PeriodTag,
  { label: string; sortOrder: number; ratioFrom: string }
> = CONFIG_PERIOD_TAG_INFO as Record<
  PeriodTag,
  { label: string; sortOrder: number; ratioFrom: string }
>;

// Default settings - imported from centralized config
// See ui/src/config/dafne/defaults.ts to modify default values
export const DEFAULT_SETTINGS: UserSettings = {
  ...CONFIG_DEFAULT_SETTINGS,
  mealTimes: { ...DEFAULT_MEAL_TIMES },
} as UserSettings;

// Entry form data (before saving)
export interface EntryFormData {
  timestamp: Date;
  periodTag: PeriodTag;
  bloodGlucose: string; // GL
  carbs: string; // CP
  qaBase: string; // Quick base
  qaCorrection: string; // Quick correction (+/-)
  biBase: string; // Long base
  biCorrection: string; // Long correction (+/-)
  ketones: string; // Ket
  notes: string; // Note
}

// Insulin-on-board (IOB) calculation
// QA insulin activity duration - from centralized config
export const QA_DURATION_HOURS = IOB_CONFIG.QA_DURATION_HOURS;

// Calculate remaining active insulin from a dose
// Uses centralized DAFNE formula from config
export const calculateIOB = configCalculateIOB;

// Correction recommendation result
export interface CorrectionRecommendation {
  suggestedCorrection: number; // Units to add/subtract
  currentBG: number; // Input BG
  targetBG: number; // Target BG used
  correctionFactor: number; // ISF used
  activeIOB: number; // Insulin still active
  iobAdjustment: number; // How much IOB reduced the correction
  rawCorrection: number; // Correction before IOB adjustment
  warning?: string; // Stacking warning if applicable
}

// Day view grouping
export interface DayData {
  date: Date;
  entries: LogEntry[];
  entriesByPeriod: Record<PeriodTag, LogEntry[]>;
}

// BG classification for traffic light display
export type BgClassification = "low" | "target" | "amber" | "high";

export function classifyBg(
  value: number,
  targetLow: number,
  targetHigh: number,
): BgClassification {
  // Thresholds from centralized DAFNE config
  if (value < BG_THRESHOLDS.HYPO) return "low";
  if (value < targetLow) return "amber";
  if (value <= targetHigh) return "target";
  if (value <= BG_THRESHOLDS.AMBER_HIGH) return "amber";
  return "high";
}

// Suggest a period tag based on current hour
export function suggestPeriodTag(hour: number): PeriodTag {
  if (hour >= 5 && hour < 10) return "B";
  if (hour >= 10 && hour < 15) return "L";
  if (hour >= 15 && hour < 21) return "EM";
  if (hour >= 21 && hour < 24) return "BT";
  return "3AM"; // 0-5
}

/**
 * Parse HH:MM time string to minutes since midnight
 */
function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + (minutes || 0);
}

/**
 * Smart period suggestion based on:
 * 1. User's typical meal times (from settings)
 * 2. Existing entries for matching day context
 * 3. Closest meal period that doesn't already have an entry
 *
 * Returns the best period tag and whether it's an "after" period
 */
export interface PeriodSuggestion {
  periodTag: PeriodTag;
  confidence: "exact" | "close" | "fallback";
  reason?: string;
}

export function suggestPeriodTagSmart(
  timestamp: Date,
  mealTimes: MealTimes = DEFAULT_MEAL_TIMES,
  existingPeriods: PeriodTag[] = [],
): PeriodSuggestion {
  const timeMinutes = timestamp.getHours() * 60 + timestamp.getMinutes();

  // Define meal periods with their times and allowed window (90 min before/after)
  const mealPeriods: {
    period: PeriodTag;
    afterPeriod: PeriodTag | null;
    timeMinutes: number;
  }[] = [
    {
      period: "B",
      afterPeriod: "after-B",
      timeMinutes: parseTimeToMinutes(mealTimes.breakfast),
    },
    {
      period: "L",
      afterPeriod: "after-L",
      timeMinutes: parseTimeToMinutes(mealTimes.lunch),
    },
    {
      period: "EM",
      afterPeriod: "after-EM",
      timeMinutes: parseTimeToMinutes(mealTimes.dinner),
    },
    {
      period: "BT",
      afterPeriod: null,
      timeMinutes: parseTimeToMinutes(mealTimes.bedtime),
    },
  ];

  // Handle 3AM check (midnight to wake up)
  const wakeUpMinutes = parseTimeToMinutes(mealTimes.wakeUp);
  if (timeMinutes < wakeUpMinutes - 60) {
    return { periodTag: "3AM", confidence: "exact" };
  }

  // Find closest meal period
  let closestMeal = mealPeriods[0];
  let closestDistance = Math.abs(timeMinutes - mealPeriods[0].timeMinutes);

  for (const meal of mealPeriods) {
    const distance = Math.abs(timeMinutes - meal.timeMinutes);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestMeal = meal;
    }
  }

  // If within 90 minutes of the meal time
  if (closestDistance <= 90) {
    // Check if main period already exists
    if (existingPeriods.includes(closestMeal.period)) {
      // Use "after" period if available
      if (closestMeal.afterPeriod) {
        return {
          periodTag: closestMeal.afterPeriod,
          confidence: "close",
          reason: `${closestMeal.period} already exists`,
        };
      }
    }
    return {
      periodTag: closestMeal.period,
      confidence: closestDistance <= 30 ? "exact" : "close",
    };
  }

  // Between meals - check if we're after a meal but before the next
  // Sort meals by time
  const sortedMeals = [...mealPeriods].sort(
    (a, b) => a.timeMinutes - b.timeMinutes,
  );

  for (let i = 0; i < sortedMeals.length - 1; i++) {
    const current = sortedMeals[i];
    const next = sortedMeals[i + 1];

    if (timeMinutes > current.timeMinutes && timeMinutes < next.timeMinutes) {
      // We're between these two meals
      // If closer to current meal's end, suggest "after-X"
      const midpoint = (current.timeMinutes + next.timeMinutes) / 2;
      if (timeMinutes < midpoint && current.afterPeriod) {
        if (
          existingPeriods.includes(current.period) &&
          existingPeriods.includes(current.afterPeriod)
        ) {
          // Both exist, fall back to simple hour-based
          return {
            periodTag: suggestPeriodTag(timestamp.getHours()),
            confidence: "fallback",
          };
        }
        return {
          periodTag: current.afterPeriod,
          confidence: "close",
          reason: `Between ${current.period} and ${next.period}`,
        };
      }
    }
  }

  // Fallback to simple hour-based suggestion
  return {
    periodTag: suggestPeriodTag(timestamp.getHours()),
    confidence: "fallback",
  };
}

// Exercise-based insulin adjustment recommendations (DAFNE principles)
export interface ExerciseRecommendation {
  qaReduction: number; // Percentage to reduce QA (0-100)
  extraCarbs: number; // Alternative: extra carbs without insulin
  timing: "before" | "after" | "during";
  reason: string;
  warnings: string[];
}

/**
 * Calculate exercise-based insulin adjustment recommendation
 * Based on DAFNE principles - values from centralized config:
 * ui/src/config/dafne/insulin.ts
 */
export function getExerciseRecommendation(
  exercise: ExerciseData,
  timing: "before" | "after",
  periodTag: PeriodTag,
): ExerciseRecommendation {
  const warnings: string[] = [];
  let qaReduction = 0;
  let extraCarbs = 0;
  let reason = "";

  // Calculate based on intensity and duration
  const durationFactor = Math.min(exercise.duration / 60, 2); // Cap at 2 hours worth
  const { PRE_EXERCISE, POST_EXERCISE, CAPS, EXTRA_CARBS } =
    EXERCISE_ADJUSTMENTS;

  if (timing === "before") {
    // Recommendations for insulin with upcoming meal before exercise
    switch (exercise.intensity) {
      case "light":
        qaReduction = Math.round(PRE_EXERCISE.LIGHT * durationFactor);
        extraCarbs = Math.round(EXTRA_CARBS.PRE_LIGHT * durationFactor);
        reason = "Light activity slightly increases insulin sensitivity";
        break;
      case "moderate":
        qaReduction = Math.round(PRE_EXERCISE.MODERATE * durationFactor);
        extraCarbs = Math.round(EXTRA_CARBS.PRE_MODERATE * durationFactor);
        reason =
          "Moderate exercise significantly increases insulin sensitivity";
        break;
      case "vigorous":
        qaReduction = Math.round(PRE_EXERCISE.VIGOROUS * durationFactor);
        extraCarbs = Math.round(EXTRA_CARBS.PRE_VIGOROUS * durationFactor);
        reason = "Vigorous exercise greatly increases insulin sensitivity";
        warnings.push("Monitor BG closely during and after activity");
        break;
    }

    // Cap reductions
    qaReduction = Math.min(qaReduction, CAPS.PRE_EXERCISE_MAX);
    extraCarbs = Math.min(extraCarbs, EXTRA_CARBS.PRE_MAX);
  } else {
    // Post-exercise recommendations
    switch (exercise.intensity) {
      case "light":
        qaReduction = Math.round(POST_EXERCISE.LIGHT * durationFactor);
        extraCarbs = Math.round(EXTRA_CARBS.POST_LIGHT * durationFactor);
        reason = "Post-exercise insulin sensitivity remains elevated";
        break;
      case "moderate":
        qaReduction = Math.round(POST_EXERCISE.MODERATE * durationFactor);
        extraCarbs = Math.round(EXTRA_CARBS.POST_MODERATE * durationFactor);
        reason = "Muscles continue to take up glucose after exercise";
        warnings.push(
          "Consider snack before bed if exercised in afternoon/evening",
        );
        break;
      case "vigorous":
        qaReduction = Math.round(POST_EXERCISE.VIGOROUS * durationFactor);
        extraCarbs = Math.round(EXTRA_CARBS.POST_VIGOROUS * durationFactor);
        reason = "Significant post-exercise glucose uptake continues for hours";
        warnings.push("High risk of delayed hypo - check BG before bed");
        break;
    }

    qaReduction = Math.min(qaReduction, CAPS.POST_EXERCISE_MAX);
    extraCarbs = Math.min(extraCarbs, EXTRA_CARBS.POST_MAX);
  }

  // Evening exercise warnings
  if (
    (periodTag === "EM" || periodTag === "BT") &&
    exercise.intensity !== "light"
  ) {
    warnings.push("Evening exercise increases overnight hypo risk");
    warnings.push(
      "Consider reducing basal insulin or having an uncovered bedtime snack",
    );
  }

  return {
    qaReduction,
    extraCarbs,
    timing,
    reason,
    warnings,
  };
}

/**
 * Check for recent exercise that might affect current insulin needs
 * Returns exercise entries from the past 4 hours
 */
/**
 * Get recent exercise entries that may still affect insulin sensitivity
 * DAFNE rules: Post-exercise effects can last 12-24 hours
 * - Moderate: 4-6 hours effect
 * - Vigorous: 6-12 hours effect
 * - Prolonged: 12-24 hours effect
 * Uses 12-hour window to capture most exercise-related insulin adjustments
 */
export function getRecentExercise(
  entries: LogEntry[],
  now: Date = new Date(),
): LogEntry[] {
  const twelveHoursAgo = now.getTime() - 12 * 60 * 60 * 1000;
  return entries.filter(
    (e) => e.exercise && getTimestampMs(e.timestamp) >= twelveHoursAgo,
  );
}

// ============ KETONE ALERTS (DAFNE Rules) ============

/**
 * Ketone alert levels based on DAFNE guidelines
 * - < 0.6 mmol/L: Normal
 * - 0.6 - 1.5 mmol/L: Slightly elevated - action needed
 * - 1.5 - 3.0 mmol/L: Risk of DKA - urgent action
 * - > 3.0 mmol/L: High risk of DKA - seek medical help
 */
export type KetoneAlertLevel = "normal" | "elevated" | "high" | "danger";

export interface KetoneAlert {
  level: KetoneAlertLevel;
  title: string;
  warnings: string[];
  actions: string[];
  seekMedicalHelp: boolean;
  recheckMinutes: number; // When to recheck ketones
  extraCorrectionPercent: number; // 0, 10, 20, etc.
}

/**
 * Get ketone alerts based on DAFNE rules
 * Shows guidance when ketones are entered, independent of sick day status
 * Thresholds from centralized config: ui/src/config/dafne/thresholds.ts
 */
export function getKetoneAlert(
  ketones: number,
  bloodGlucose?: number,
): KetoneAlert {
  // Normal ketones
  if (ketones < KETONE_THRESHOLDS.NORMAL_MAX) {
    return {
      level: "normal",
      title: "Ketones Normal",
      warnings: [],
      actions: [],
      seekMedicalHelp: false,
      recheckMinutes: 0, // No recheck needed
      extraCorrectionPercent: 0,
    };
  }

  // Slightly elevated (0.6 - 1.5)
  if (ketones < KETONE_THRESHOLDS.ELEVATED_MAX) {
    const actions = [
      "Drink plenty of water or sugar-free fluids",
      "Check blood glucose",
      "Recheck ketones in 2-4 hours",
    ];
    const warnings: string[] = [];

    if (
      bloodGlucose &&
      bloodGlucose > KETONE_BG_COMBINED.ELEVATED_KETONE_HIGH_BG
    ) {
      warnings.push("Elevated ketones with high BG - monitor closely");
      actions.push(
        `Consider ${KETONE_BG_COMBINED.ELEVATED_EXTRA_CORRECTION}% extra on correction dose`,
      );
    }

    return {
      level: "elevated",
      title: "Ketones Slightly Elevated",
      warnings,
      actions,
      seekMedicalHelp: false,
      recheckMinutes: KETONE_RECHECK_MINUTES.ELEVATED,
      extraCorrectionPercent:
        bloodGlucose &&
        bloodGlucose > KETONE_BG_COMBINED.ELEVATED_KETONE_HIGH_BG
          ? KETONE_BG_COMBINED.ELEVATED_EXTRA_CORRECTION
          : 0,
    };
  }

  // High ketones - risk of DKA (1.5 - 3.0)
  if (ketones < KETONE_THRESHOLDS.HIGH_MAX) {
    const warnings = [
      "⚠️ Risk of DKA (Diabetic Ketoacidosis)",
      "Do NOT exercise with elevated ketones",
    ];
    const actions = [
      `Take correction insulin with ${KETONE_BG_COMBINED.HIGH_EXTRA_CORRECTION}% extra`,
      "Drink plenty of fluids - at least 200ml per hour",
      "Recheck ketones in 2 hours",
      "Contact diabetes team if not improving",
    ];

    if (
      bloodGlucose &&
      bloodGlucose > KETONE_BG_COMBINED.ELEVATED_KETONE_HIGH_BG
    ) {
      warnings.push("High BG + high ketones requires urgent attention");
    }

    return {
      level: "high",
      title: "High Ketones - DKA Risk",
      warnings,
      actions,
      seekMedicalHelp: ketones >= KETONE_THRESHOLDS.SEEK_HELP,
      recheckMinutes: KETONE_RECHECK_MINUTES.HIGH,
      extraCorrectionPercent: KETONE_BG_COMBINED.HIGH_EXTRA_CORRECTION,
    };
  }

  // Danger - very high ketones (>= 3.0)
  return {
    level: "danger",
    title: "DANGER: Very High Ketones",
    warnings: [
      "🚨 HIGH RISK OF DKA - SEEK MEDICAL HELP",
      "Do NOT delay - this is a medical emergency",
      "Do NOT exercise",
    ],
    actions: [
      "Contact your diabetes team or go to A&E immediately",
      "Take correction insulin if advised by healthcare team",
      "Keep drinking fluids while waiting",
      "Have someone stay with you",
    ],
    seekMedicalHelp: true,
    recheckMinutes: KETONE_RECHECK_MINUTES.DANGER,
    extraCorrectionPercent: KETONE_BG_COMBINED.HIGH_EXTRA_CORRECTION,
  };
}

/**
 * Get BG + Ketone combined alert for more nuanced guidance
 * Used when both values are available during entry
 */
export function getBgKetoneAlert(
  bloodGlucose: number,
  ketones?: number,
): { shouldCheckKetones: boolean; reason?: string } {
  // Recommend ketone check for high BG
  if (bloodGlucose > 14 && ketones === undefined) {
    return {
      shouldCheckKetones: true,
      reason: "BG > 14 mmol/L - check ketones if you haven't recently",
    };
  }

  if (bloodGlucose > 17) {
    return {
      shouldCheckKetones: true,
      reason: "Very high BG - ketone check recommended",
    };
  }

  return { shouldCheckKetones: false };
}

// ============ SICK DAY MANAGEMENT (DAFNE Rules) ============

/**
 * Sick day recommendation result
 * Based on DAFNE sick day rules:
 * - NEVER stop insulin, even if not eating
 * - Check BG every 2-4 hours
 * - Check ketones if BG > 14 or feeling unwell
 * - May need 10-20% more correction insulin
 * - Stay hydrated - sugary drinks if can't eat solids
 * - Seek medical help if: ketones > 3.0, persistent vomiting, BG > 14 for 8+ hours
 */
export interface SickDayRecommendation {
  correctionMultiplier: number; // 1.1-1.2 (10-20% increase)
  nextBgCheckMinutes: number; // Minutes until next recommended check
  checkKetones: boolean; // Should check ketones?
  warnings: string[];
  actions: string[];
  seekMedicalHelp: boolean;
}

/**
 * Check if currently in sick day mode
 * Returns the most recent sick day data, or null if not sick
 * Logic: If the most recent entry has sickDay, user is sick.
 *        If the most recent entry doesn't have sickDay, user is not sick.
 */
export function getCurrentSickStatus(entries: LogEntry[]): SickDayData | null {
  if (entries.length === 0) return null;

  // Sort by timestamp, most recent first
  const sorted = [...entries].sort(
    (a, b) => getTimestampMs(b.timestamp) - getTimestampMs(a.timestamp),
  );

  // Check the most recent entry
  const mostRecent = sorted[0];
  return mostRecent.sickDay ?? null;
}

/**
 * Get sick day recommendations based on current BG and ketone levels
 */
export function getSickDayRecommendation(
  bloodGlucose: number | undefined,
  ketones: number | undefined,
  hoursSick: number, // How many hours since "I'm sick" entry
): SickDayRecommendation {
  const warnings: string[] = [];
  const actions: string[] = [];
  let seekMedicalHelp = false;
  let correctionMultiplier = 1.1; // Default 10% increase
  let nextBgCheckMinutes = 240; // Default 4 hours
  let checkKetones = false;

  // BG-based recommendations
  if (bloodGlucose !== undefined) {
    if (bloodGlucose > 17) {
      // Very high BG
      correctionMultiplier = 1.2;
      nextBgCheckMinutes = 120; // Check every 2 hours
      checkKetones = true;
      warnings.push("Very high BG - check ketones immediately");
      actions.push("Take correction dose with 20% extra");
      actions.push("Drink plenty of sugar-free fluids");

      if (hoursSick > 8) {
        warnings.push("BG has been elevated for extended period");
        seekMedicalHelp = true;
      }
    } else if (bloodGlucose > 14) {
      // High BG
      correctionMultiplier = 1.15;
      nextBgCheckMinutes = 180; // Check every 3 hours
      checkKetones = true;
      warnings.push("High BG when sick - check ketones");
      actions.push("Take correction dose with 15% extra");
      actions.push("Stay well hydrated");
    } else if (bloodGlucose > 10) {
      // Elevated BG
      correctionMultiplier = 1.1;
      nextBgCheckMinutes = 240; // Check every 4 hours
      actions.push("Consider small correction with 10% extra");
    } else if (bloodGlucose < 4) {
      // Hypo when sick
      warnings.push("Hypo while sick - treat immediately");
      actions.push("Take 15-20g fast-acting carbs");
      actions.push("Recheck in 15 minutes");
      nextBgCheckMinutes = 15;
      correctionMultiplier = 0.9; // Reduce next correction
    } else if (bloodGlucose < 5) {
      // Low-normal - risk of hypo
      warnings.push("BG trending low - be cautious with corrections");
      actions.push("Have carbs available");
      nextBgCheckMinutes = 180;
      correctionMultiplier = 1.0; // No increase
    }
  } else {
    // No BG reading provided
    nextBgCheckMinutes = 120;
    actions.push("Check BG as soon as possible when sick");
    checkKetones = true;
  }

  // Ketone-based recommendations
  if (ketones !== undefined) {
    if (ketones >= 3.0) {
      warnings.push("DANGER: High ketones detected");
      actions.push("Seek medical help immediately");
      actions.push("Do not exercise");
      seekMedicalHelp = true;
      nextBgCheckMinutes = 60;
    } else if (ketones >= 1.5) {
      warnings.push("Elevated ketones - risk of DKA");
      actions.push("Take extra correction insulin (20% more)");
      actions.push("Drink plenty of fluids");
      actions.push("Recheck ketones in 2 hours");
      correctionMultiplier = 1.2;
      nextBgCheckMinutes = 120;

      if (bloodGlucose && bloodGlucose > 14) {
        warnings.push("High BG + elevated ketones - monitor closely");
        seekMedicalHelp = ketones >= 2.5;
      }
    } else if (ketones >= 0.6) {
      warnings.push("Ketones slightly elevated");
      actions.push("Stay hydrated and monitor");
      checkKetones = true;
    }
  }

  // General sick day actions
  if (!seekMedicalHelp) {
    actions.push("Never stop taking insulin when sick");
    actions.push("Sip fluids regularly - at least 100ml/hour");

    if (hoursSick < 4) {
      actions.push("Rest and avoid strenuous activity");
    }
  }

  // When to seek help
  if (seekMedicalHelp) {
    warnings.unshift("⚠️ SEEK MEDICAL HELP");
    actions.unshift("Contact your diabetes team or go to A&E");
  }

  return {
    correctionMultiplier,
    nextBgCheckMinutes,
    checkKetones,
    warnings,
    actions,
    seekMedicalHelp,
  };
}

/**
 * Format next check time as human-readable string
 */
export function formatNextCheckTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  return `${hours}h ${mins}m`;
}

/**
 * Calculate when the next BG check is due
 */
export function getNextCheckTime(baseTime: Date, minutes: number): Date {
  return new Date(baseTime.getTime() + minutes * 60 * 1000);
}

// ============================================
// CGM Multi-Source Architecture
// ============================================

/**
 * CGM data source identifier
 * Used to track where data originated for audit and preference
 */
export type CgmSource = "libreview" | "dexcom" | "manual" | "other";

/**
 * CGM reading type
 * - historic: Automatic CGM reading (every 5-15 min depending on device)
 * - scan: Manual sensor scan by user
 * - fingerstick: Blood glucose meter reading
 * - manual: Manually entered value
 */
export type CgmReadingType = "historic" | "scan" | "fingerstick" | "manual";

/**
 * Priority for reading types when deduplicating
 * Higher number = higher priority (takes precedence)
 */
export const CGM_READING_TYPE_PRIORITY: Record<CgmReadingType, number> = {
  historic: 1,
  scan: 2,
  fingerstick: 3,
  manual: 4,
};

/**
 * CGM Import metadata
 * Tracks each import operation for audit and rollback capability
 */
export interface CgmImport {
  id: string;
  source: CgmSource;
  importedAt: Date;
  fileName?: string;
  dateRange: {
    start: Date;
    end: Date;
  };
  readingCount: number;
  deviceSerials: string[];
  /** Original file hash for duplicate detection */
  fileHash?: string;
  /** User notes about this import */
  notes?: string;
}

/**
 * Raw CGM reading - preserves original data from source
 * Stored separately from unified readings for audit purposes
 */
export interface CgmRawReading {
  id: string;
  importId: string;
  source: CgmSource;
  timestamp: Date;
  glucose: number; // Always mmol/L (converted during import)
  glucoseOriginal: number; // Original value before conversion
  glucoseUnit: "mg/dL" | "mmol/L";
  type: CgmReadingType;
  deviceSerial?: string;
  /** Original row data as JSON for debugging */
  rawData?: string;
}

/**
 * Unified CGM reading - single source of truth for analysis
 * Created by merging/deduplicating raw readings
 */
export interface CgmReading {
  id: string;
  timestamp: Date;
  glucose: number; // Always mmol/L
  type: CgmReadingType;
  source: CgmSource;
  deviceSerial?: string;
  createdAt: Date;
  /** Link to the import that created this reading */
  importId?: string;
  /** Link to the raw reading this was derived from */
  rawReadingId?: string;
  /** If multiple sources had readings at this time, which ones */
  alternativeSources?: CgmSource[];
}

/**
 * Time buffer for considering readings as duplicates (in milliseconds)
 * Libre: ~15 min intervals, Dexcom: ~5 min intervals
 * Using 2.5 minutes handles natural timing variations
 */
export const CGM_DEDUP_BUFFER_MS = 2.5 * 60 * 1000;

/**
 * Merge CGM readings from multiple sources into a unified stream
 * Handles deduplication and conflict resolution
 *
 * @param readings All readings to merge (may have duplicates/overlaps)
 * @returns Deduplicated unified readings
 */
export function mergeCgmReadings(readings: CgmReading[]): CgmReading[] {
  if (readings.length === 0) return [];

  // Sort by timestamp
  const sorted = [...readings].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  const merged: CgmReading[] = [];
  let currentGroup: CgmReading[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const reading = sorted[i];
    const groupStart = currentGroup[0].timestamp.getTime();

    if (reading.timestamp.getTime() - groupStart <= CGM_DEDUP_BUFFER_MS) {
      // Within buffer - add to current group
      currentGroup.push(reading);
    } else {
      // New group - resolve current group and start new one
      merged.push(resolveReadingConflict(currentGroup));
      currentGroup = [reading];
    }
  }

  // Don't forget the last group
  merged.push(resolveReadingConflict(currentGroup));

  return merged;
}

/**
 * Resolve conflict when multiple readings exist for the same time window
 * Priority: manual > fingerstick > scan > historic
 * Secondary: prefer more recent import
 */
function resolveReadingConflict(group: CgmReading[]): CgmReading {
  if (group.length === 1) return group[0];

  // Sort by priority (highest first), then by createdAt (newest first)
  const sorted = group.sort((a, b) => {
    const priorityDiff =
      CGM_READING_TYPE_PRIORITY[b.type] - CGM_READING_TYPE_PRIORITY[a.type];
    if (priorityDiff !== 0) return priorityDiff;
    return b.createdAt.getTime() - a.createdAt.getTime();
  });

  const winner = sorted[0];
  const alternativeSources = [
    ...new Set(group.filter((r) => r !== winner).map((r) => r.source)),
  ];

  return {
    ...winner,
    alternativeSources:
      alternativeSources.length > 0 ? alternativeSources : undefined,
  };
}

/**
 * Find gaps in CGM data where user may have switched devices
 * Useful for showing users when data sources changed
 */
export function findCgmSourceTransitions(
  readings: CgmReading[],
): Array<{ date: Date; from: CgmSource; to: CgmSource }> {
  if (readings.length < 2) return [];

  const sorted = [...readings].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  const transitions: Array<{ date: Date; from: CgmSource; to: CgmSource }> = [];
  let currentSource = sorted[0].source;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].source !== currentSource) {
      transitions.push({
        date: sorted[i].timestamp,
        from: currentSource,
        to: sorted[i].source,
      });
      currentSource = sorted[i].source;
    }
  }

  return transitions;
}

/**
 * Get statistics about CGM data by source
 */
export function getCgmSourceStats(
  readings: CgmReading[],
): Record<
  CgmSource,
  { count: number; dateRange?: { start: Date; end: Date } }
> {
  const stats: Record<
    CgmSource,
    { count: number; dateRange?: { start: Date; end: Date } }
  > = {
    libreview: { count: 0 },
    dexcom: { count: 0 },
    manual: { count: 0 },
    other: { count: 0 },
  };

  for (const reading of readings) {
    const sourceStat = stats[reading.source];
    sourceStat.count++;

    if (!sourceStat.dateRange) {
      sourceStat.dateRange = {
        start: reading.timestamp,
        end: reading.timestamp,
      };
    } else {
      if (reading.timestamp < sourceStat.dateRange.start) {
        sourceStat.dateRange.start = reading.timestamp;
      }
      if (reading.timestamp > sourceStat.dateRange.end) {
        sourceStat.dateRange.end = reading.timestamp;
      }
    }
  }

  return stats;
}

/**
 * Create a CgmImport record for tracking an import operation
 */
export function createCgmImport(
  source: CgmSource,
  readings: CgmReading[],
  fileName?: string,
  fileHash?: string,
): CgmImport {
  const deviceSerials = [
    ...new Set(readings.map((r) => r.deviceSerial).filter(Boolean)),
  ] as string[];

  const timestamps = readings.map((r) => r.timestamp.getTime());
  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);

  return {
    id: crypto.randomUUID(),
    source,
    importedAt: new Date(),
    fileName,
    dateRange: {
      start: new Date(minTime),
      end: new Date(maxTime),
    },
    readingCount: readings.length,
    deviceSerials,
    fileHash,
  };
}

/**
 * Convert LibreView rows to raw readings for archival
 */
export function libreViewToRawReadings(
  rows: LibreViewRow[],
  importId: string,
  glucoseUnit: "mg/dL" | "mmol/L",
): CgmRawReading[] {
  const readings: CgmRawReading[] = [];

  for (const row of rows) {
    let glucose: number | undefined;
    let glucoseOriginal: number | undefined;
    let type: CgmReadingType;

    if (row.recordType === 0 && row.historicGlucose !== undefined) {
      glucose = row.historicGlucose;
      glucoseOriginal =
        glucoseUnit === "mg/dL"
          ? row.historicGlucose * 18.0182
          : row.historicGlucose;
      type = "historic";
    } else if (row.recordType === 1 && row.scanGlucose !== undefined) {
      glucose = row.scanGlucose;
      glucoseOriginal =
        glucoseUnit === "mg/dL" ? row.scanGlucose * 18.0182 : row.scanGlucose;
      type = "scan";
    } else if (row.recordType === 2 && row.stripGlucose !== undefined) {
      glucose = row.stripGlucose;
      glucoseOriginal =
        glucoseUnit === "mg/dL" ? row.stripGlucose * 18.0182 : row.stripGlucose;
      type = "fingerstick";
    } else {
      continue; // Skip non-glucose records
    }

    readings.push({
      id: crypto.randomUUID(),
      importId,
      source: "libreview",
      timestamp: row.deviceTimestamp,
      glucose,
      glucoseOriginal,
      glucoseUnit,
      type,
      deviceSerial: row.serialNumber,
      rawData: JSON.stringify(row),
    });
  }

  return readings;
}

/**
 * Convert Dexcom rows to raw readings for archival
 */
export function dexcomToRawReadings(
  rows: DexcomRow[],
  importId: string,
): CgmRawReading[] {
  const readings: CgmRawReading[] = [];

  for (const row of rows) {
    if (!row.timestamp || row.glucoseValue === undefined) continue;

    let type: CgmReadingType;
    if (row.eventType === "EGV") {
      type = "historic";
    } else if (row.eventType === "Calibration") {
      type = "fingerstick";
    } else {
      continue; // Skip non-glucose records
    }

    // Dexcom is always in mg/dL, we store in mmol/L
    const glucoseOriginal = row.glucoseValue * 18.0182; // Convert back to original

    readings.push({
      id: crypto.randomUUID(),
      importId,
      source: "dexcom",
      timestamp: row.timestamp,
      glucose: row.glucoseValue,
      glucoseOriginal,
      glucoseUnit: "mg/dL",
      type,
      deviceSerial: row.transmitterId,
      rawData: JSON.stringify(row),
    });
  }

  return readings;
}

/**
 * LibreView CSV record types
 * Record Type 0 = Historic glucose
 * Record Type 1 = Scan glucose
 * Record Type 2 = Strip glucose
 * Record Type 3 = Insulin
 * Record Type 4 = Food
 * Record Type 5 = Notes
 * Record Type 6 = Activity
 */
export type LibreViewRecordType = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Raw LibreView CSV row (after parsing)
 *
 * Column reference:
 * 0: Device, 1: Serial Number, 2: Device Timestamp, 3: Record Type
 * 4: Historic Glucose, 5: Scan Glucose, 6: Non-numeric Rapid-Acting Insulin
 * 7: Rapid-Acting Insulin (units), 8: Non-numeric Food, 9: Carbohydrates (grams)
 * 10: Carbohydrates (servings), 11: Non-numeric Long-Acting Insulin
 * 12: Long-Acting Insulin (units), 13: Notes, 14: Strip Glucose
 * 15: Ketone mmol/L, 16: Meal Insulin (units), 17: Correction Insulin (units)
 * 18: User Change Insulin (units)
 */
export interface LibreViewRow {
  device: string;
  serialNumber: string;
  deviceTimestamp: Date;
  recordType: LibreViewRecordType;
  historicGlucose?: number; // mmol/L after conversion
  scanGlucose?: number; // mmol/L after conversion
  stripGlucose?: number; // mmol/L after conversion
  rapidActingInsulin?: number; // Total rapid insulin (column 7)
  longActingInsulinValue?: number; // Long-acting insulin (column 12)
  nonNumericFood?: string; // Free text in food column - may contain "Exercise" (column 8)
  foodCarbs?: number; // Carbohydrates in grams (column 9)
  notes?: string; // Free text notes (column 13)
  ketones?: number; // Ketone mmol/L (column 15)
  mealInsulin?: number; // Split meal dose (column 16)
  correctionInsulin?: number; // Split correction dose (column 17)
  userChangeInsulin?: number; // User adjustment (column 18)
}

/**
 * LibreView file validation result
 */
export interface LibreViewValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  recordCount: number;
  dateRange?: {
    start: Date;
    end: Date;
  };
  glucoseUnit: "mg/dL" | "mmol/L";
}

/**
 * Convert mg/dL to mmol/L
 */
export function mgdlToMmol(mgdl: number): number {
  return Math.round((mgdl / 18.0182) * 10) / 10;
}

/**
 * Convert mmol/L to mg/dL
 */
export function mmolToMgdl(mmol: number): number {
  return Math.round(mmol * 18.0182);
}

/**
 * Parse LibreView CSV header to detect format
 */
export function parseLibreViewHeader(headerLine: string): LibreViewValidation {
  const result: LibreViewValidation = {
    valid: false,
    errors: [],
    warnings: [],
    recordCount: 0,
    glucoseUnit: "mg/dL",
  };

  // LibreView CSVs typically have these required columns
  const requiredColumns = ["Device", "Device Timestamp", "Record Type"];

  // Check for glucose columns (at least one should exist)
  const glucoseColumns = ["Historic Glucose", "Scan Glucose"];

  const columns = headerLine.split(",").map((c) => c.trim().replace(/"/g, ""));

  // Check required columns
  for (const req of requiredColumns) {
    if (!columns.some((c) => c.toLowerCase().includes(req.toLowerCase()))) {
      result.errors.push(`Missing required column: ${req}`);
    }
  }

  // Check for at least one glucose column
  const hasGlucose = glucoseColumns.some((gc) =>
    columns.some((c) => c.toLowerCase().includes(gc.toLowerCase())),
  );

  if (!hasGlucose) {
    result.errors.push("No glucose data columns found");
  }

  // Detect glucose unit from column headers
  // LibreView uses "Historic Glucose mg/dL" or "Historic Glucose mmol/L"
  const glucoseCol = columns.find((c) => c.toLowerCase().includes("glucose"));
  if (glucoseCol) {
    if (glucoseCol.includes("mmol/L")) {
      result.glucoseUnit = "mmol/L";
    } else {
      result.glucoseUnit = "mg/dL";
    }
  }

  result.valid = result.errors.length === 0;
  return result;
}

/**
 * Parse a LibreView CSV row
 */
export function parseLibreViewRow(
  row: string,
  columnIndices: Record<string, number>,
  glucoseUnit: "mg/dL" | "mmol/L",
): LibreViewRow | null {
  // Split handling quoted values
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (const char of row) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());

  const getValue = (key: string): string | undefined => {
    const idx = columnIndices[key];
    return idx !== undefined ? values[idx] : undefined;
  };

  const getNumericValue = (key: string): number | undefined => {
    const val = getValue(key);
    if (!val || val === "") return undefined;
    const num = parseFloat(val);
    return isNaN(num) ? undefined : num;
  };

  const deviceTimestampStr = getValue("Device Timestamp");
  if (!deviceTimestampStr) return null;

  // LibreView date format: "DD-MM-YYYY HH:MM" or "MM-DD-YYYY HH:MM AM/PM"
  // Try multiple formats
  const timestamp = parseLibreViewDate(deviceTimestampStr);
  if (!timestamp) return null;

  const recordType = getNumericValue("Record Type");
  if (recordType === undefined) return null;

  // Get glucose values and convert to internal unit if needed
  let historicGlucose =
    getNumericValue("Historic Glucose mg/dL") ||
    getNumericValue("Historic Glucose mmol/L");
  let scanGlucose =
    getNumericValue("Scan Glucose mg/dL") ||
    getNumericValue("Scan Glucose mmol/L");
  let stripGlucose =
    getNumericValue("Strip Glucose mg/dL") ||
    getNumericValue("Strip Glucose mmol/L");

  // Convert to mmol/L if source is mg/dL
  if (glucoseUnit === "mg/dL") {
    if (historicGlucose) historicGlucose = mgdlToMmol(historicGlucose);
    if (scanGlucose) scanGlucose = mgdlToMmol(scanGlucose);
    if (stripGlucose) stripGlucose = mgdlToMmol(stripGlucose);
  }

  return {
    device: getValue("Device") || "",
    serialNumber: getValue("Serial Number") || "",
    deviceTimestamp: timestamp,
    recordType: recordType as LibreViewRecordType,
    historicGlucose,
    scanGlucose,
    stripGlucose,
    rapidActingInsulin: getNumericValue("Rapid-Acting Insulin (units)"),
    longActingInsulinValue: getNumericValue("Long-Acting Insulin (units)"),
    nonNumericFood: getValue("Non-numeric Food"),
    foodCarbs:
      getNumericValue("Carbohydrates (grams)") ||
      getNumericValue("Carbohydrates (servings)") ||
      getNumericValue("Food (carbs, grams)"),
    notes: getValue("Notes"),
    ketones: getNumericValue("Ketone mmol/L"),
    mealInsulin: getNumericValue("Meal Insulin (units)"),
    correctionInsulin: getNumericValue("Correction Insulin (units)"),
    userChangeInsulin: getNumericValue("User Change Insulin (units)"),
  };
}

/**
 * Parse LibreView date formats
 */
export function parseLibreViewDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Clean the string
  dateStr = dateStr.trim().replace(/"/g, "");

  // Try DD-MM-YYYY HH:MM AM/PM format (UK/EU with 12-hour clock)
  // Check if day > 12 to distinguish from MM-DD-YYYY
  let match = dateStr.match(
    /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
  );
  if (match) {
    const [, first, second, year, hour, minute, ampm] = match;
    const firstNum = parseInt(first);
    const secondNum = parseInt(second);
    let h = parseInt(hour);

    if (ampm) {
      if (ampm.toUpperCase() === "PM" && h !== 12) h += 12;
      if (ampm.toUpperCase() === "AM" && h === 12) h = 0;
    }

    // If first number > 12, it must be a day (DD-MM-YYYY)
    // If second number > 12, it must be a day too (MM-DD-YYYY)
    // Otherwise, assume DD-MM-YYYY for LibreView exports
    if (firstNum > 12) {
      // DD-MM-YYYY format
      return new Date(
        parseInt(year),
        secondNum - 1,
        firstNum,
        h,
        parseInt(minute),
      );
    } else if (secondNum > 12) {
      // MM-DD-YYYY format
      return new Date(
        parseInt(year),
        firstNum - 1,
        secondNum,
        h,
        parseInt(minute),
      );
    } else {
      // Ambiguous - assume UK/EU format DD-MM-YYYY for LibreView
      return new Date(
        parseInt(year),
        secondNum - 1,
        firstNum,
        h,
        parseInt(minute),
      );
    }
  }

  // Try DD-MM-YYYY HH:MM format without AM/PM (24-hour clock)
  match = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{2})$/);
  if (match) {
    const [, day, month, year, hour, minute] = match;
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
    );
  }

  // Try ISO format as fallback
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) {
    return isoDate;
  }

  return null;
}

/**
 * Convert LibreView rows to CGM readings
 * @param rows Parsed LibreView rows
 * @param importId Optional import ID for tracking
 */
export function libreViewToCgmReadings(
  rows: LibreViewRow[],
  importId?: string,
): CgmReading[] {
  const readings: CgmReading[] = [];

  for (const row of rows) {
    // Record type 0 = historic glucose (auto CGM reading)
    if (row.recordType === 0 && row.historicGlucose !== undefined) {
      readings.push({
        id: `cgm-${row.deviceTimestamp.getTime()}-${row.serialNumber}`,
        timestamp: row.deviceTimestamp,
        glucose: row.historicGlucose,
        type: "historic",
        source: "libreview",
        deviceSerial: row.serialNumber,
        createdAt: new Date(),
        importId,
      });
    }
    // Record type 1 = scan glucose (manual sensor scan)
    else if (row.recordType === 1 && row.scanGlucose !== undefined) {
      readings.push({
        id: `cgm-${row.deviceTimestamp.getTime()}-${row.serialNumber}`,
        timestamp: row.deviceTimestamp,
        glucose: row.scanGlucose,
        type: "scan",
        source: "libreview",
        deviceSerial: row.serialNumber,
        createdAt: new Date(),
        importId,
      });
    }
    // Record type 2 = strip glucose (fingerstick blood glucose)
    else if (row.recordType === 2 && row.stripGlucose !== undefined) {
      readings.push({
        id: `cgm-${row.deviceTimestamp.getTime()}-${row.serialNumber}-strip`,
        timestamp: row.deviceTimestamp,
        glucose: row.stripGlucose,
        type: "fingerstick",
        source: "libreview",
        deviceSerial: row.serialNumber,
        createdAt: new Date(),
        importId,
      });
    }
  }

  return readings;
}

// ============================================
// Dexcom Data Types
// ============================================

/**
 * Dexcom CSV Event Types
 */
export type DexcomEventType =
  | "EGV" // Estimated Glucose Value (CGM reading)
  | "Calibration" // Fingerstick calibration
  | "Insulin" // Insulin dose
  | "Carbs" // Carbohydrate intake
  | "Exercise" // Exercise event
  | "Health" // Health event
  | "Alert" // Alert settings/events
  | "Notes" // User notes
  | "FirstName" // Patient info (header)
  | "LastName" // Patient info (header)
  | "Device"; // Device info (header)

/**
 * Dexcom CSV row (after parsing)
 */
export interface DexcomRow {
  index: number;
  timestamp: Date | null;
  eventType: DexcomEventType;
  eventSubtype?: string;
  glucoseValue?: number; // mg/dL in file, converted to mmol/L
  insulinValue?: number;
  carbValue?: number;
  duration?: string;
  glucoseRateOfChange?: number;
  transmitterId?: string;
  sourceDeviceId?: string;
}

/**
 * Dexcom file validation result
 */
export interface DexcomValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  recordCount: number;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

/**
 * Parse Dexcom CSV header to detect format
 */
export function parseDexcomHeader(headerLine: string): DexcomValidation {
  const result: DexcomValidation = {
    valid: false,
    errors: [],
    warnings: [],
    recordCount: 0,
  };

  const columns = headerLine.split(",").map((c) => c.trim().replace(/"/g, ""));

  // Dexcom CSVs have these columns
  const requiredColumns = ["Index", "Timestamp", "Event Type", "Glucose Value"];

  // Check required columns
  for (const req of requiredColumns) {
    const found = columns.some(
      (c) =>
        c.toLowerCase().includes(req.toLowerCase().replace(" ", "")) ||
        c
          .toLowerCase()
          .replace(/[^a-z]/g, "")
          .includes(req.toLowerCase().replace(/[^a-z]/g, "")),
    );
    if (!found) {
      result.errors.push(`Missing required column: ${req}`);
    }
  }

  result.valid = result.errors.length === 0;
  return result;
}

/**
 * Build column indices from Dexcom header
 */
export function buildDexcomColumnIndices(
  headerLine: string,
): Record<string, number> {
  const columns = headerLine.split(",").map((c) => c.trim().replace(/"/g, ""));
  const indices: Record<string, number> = {};

  const columnMappings: Record<string, string[]> = {
    Index: ["Index"],
    Timestamp: ["Timestamp (YYYY-MM-DDThh:mm:ss)", "Timestamp"],
    "Event Type": ["Event Type"],
    "Event Subtype": ["Event Subtype"],
    "Glucose Value": ["Glucose Value (mg/dL)", "Glucose Value"],
    "Insulin Value": ["Insulin Value (u)", "Insulin Value"],
    "Carb Value": ["Carb Value (grams)", "Carb Value"],
    Duration: ["Duration (hh:mm:ss)", "Duration"],
    "Glucose Rate of Change": [
      "Glucose Rate of Change (mg/dL/min)",
      "Glucose Rate of Change",
    ],
    "Transmitter ID": ["Transmitter ID"],
    "Source Device ID": ["Source Device ID"],
  };

  for (const [key, aliases] of Object.entries(columnMappings)) {
    for (const alias of aliases) {
      const idx = columns.findIndex(
        (c) => c.toLowerCase() === alias.toLowerCase(),
      );
      if (idx !== -1) {
        indices[key] = idx;
        break;
      }
    }
  }

  return indices;
}

/**
 * Parse a Dexcom CSV row
 */
export function parseDexcomRow(
  row: string,
  columnIndices: Record<string, number>,
): DexcomRow | null {
  // Split handling quoted values
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (const char of row) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());

  const getValue = (key: string): string | undefined => {
    const idx = columnIndices[key];
    return idx !== undefined ? values[idx]?.replace(/"/g, "") : undefined;
  };

  const getNumericValue = (key: string): number | undefined => {
    const val = getValue(key);
    if (!val || val === "") return undefined;
    const num = parseFloat(val);
    return isNaN(num) ? undefined : num;
  };

  const indexStr = getValue("Index");
  if (!indexStr) return null;
  const index = parseInt(indexStr);
  if (isNaN(index)) return null;

  const eventType = getValue("Event Type");
  if (!eventType) return null;

  // Skip non-data rows (patient info, device info, alerts config)
  const skipTypes = ["FirstName", "LastName", "Device", "Alert"];
  if (skipTypes.includes(eventType)) return null;

  // Parse timestamp - Dexcom uses ISO format: "YYYY-MM-DDThh:mm:ss"
  const timestampStr = getValue("Timestamp");
  let timestamp: Date | null = null;
  if (timestampStr && timestampStr.length > 0) {
    timestamp = new Date(timestampStr);
    if (isNaN(timestamp.getTime())) {
      timestamp = null;
    }
  }

  // Get glucose value and convert from mg/dL to mmol/L
  let glucoseValue = getNumericValue("Glucose Value");
  if (glucoseValue !== undefined) {
    glucoseValue = mgdlToMmol(glucoseValue);
  }

  return {
    index,
    timestamp,
    eventType: eventType as DexcomEventType,
    eventSubtype: getValue("Event Subtype"),
    glucoseValue,
    insulinValue: getNumericValue("Insulin Value"),
    carbValue: getNumericValue("Carb Value"),
    duration: getValue("Duration"),
    glucoseRateOfChange: getNumericValue("Glucose Rate of Change"),
    transmitterId: getValue("Transmitter ID"),
    sourceDeviceId: getValue("Source Device ID"),
  };
}

/**
 * Convert Dexcom rows to CGM readings
 * @param rows Parsed Dexcom rows
 * @param importId Optional import ID for tracking
 */
export function dexcomToCgmReadings(
  rows: DexcomRow[],
  importId?: string,
): CgmReading[] {
  const readings: CgmReading[] = [];

  for (const row of rows) {
    // EGV = Estimated Glucose Value (CGM reading)
    if (
      row.eventType === "EGV" &&
      row.glucoseValue !== undefined &&
      row.timestamp
    ) {
      readings.push({
        id: `cgm-dexcom-${row.timestamp.getTime()}-${row.transmitterId || row.index}`,
        timestamp: row.timestamp,
        glucose: row.glucoseValue,
        type: "historic",
        source: "dexcom",
        deviceSerial: row.transmitterId,
        createdAt: new Date(),
        importId,
      });
    }
    // Calibration events use fingerstick blood glucose
    else if (
      row.eventType === "Calibration" &&
      row.glucoseValue !== undefined &&
      row.timestamp
    ) {
      readings.push({
        id: `cgm-dexcom-cal-${row.timestamp.getTime()}-${row.transmitterId || row.index}`,
        timestamp: row.timestamp,
        glucose: row.glucoseValue,
        type: "fingerstick",
        source: "dexcom",
        deviceSerial: row.transmitterId,
        createdAt: new Date(),
        importId,
      });
    }
  }

  return readings;
}

// ============================================
// LibreView Event Reconciliation Types
// ============================================

/**
 * LibreView non-CGM event types
 * Record Type 4 = Insulin (rapid or long-acting)
 * Record Type 5 = Carbohydrates
 * Record Type 6 = Activity/Exercise
 */
export type LibreViewEventType = "insulin" | "carbs" | "notes" | "exercise";

/**
 * LibreView event data extracted from rows
 */
export interface LibreViewEvent {
  timestamp: Date;
  type: LibreViewEventType;
  rapidInsulin?: number;
  longInsulin?: number;
  carbs?: number; // grams
  notes?: string;
  exercise?: boolean; // True if exercise entry detected
  source: "libreview";
}

/**
 * Patterns that can be extracted from notes
 */
export interface ParsedNotePatterns {
  // Correction pattern: cr+5, cr-3, +2cr, -1cr
  correction?: number;
  // Carb patterns: 30g, 30 grams, 30g carbs
  carbs?: number;
  // CP patterns: 3cp, 3 cp, cp3
  cp?: number;
  // Insulin patterns: 9u, 9 units, 9units
  insulin?: number;
  // Full dose pattern: 7cp =21u + 1cr
  doseBreakdown?: {
    cp: number;
    baseUnits: number;
    correction: number;
  };
  // Glucose reading: GL12.7, bg 12.7, glucose 12.7
  glucose?: number;
  // Exercise parsed from note: "exercise moderate 15min"
  hasExercise?: boolean;
  exerciseIntensity?: ExerciseIntensity;
  exerciseDuration?: number; // minutes
  // Sick/unwell mention
  hasSick?: boolean;
  // Ketone pattern: ket 1.2, ketones 0.5
  ketones?: number;
  // Remaining unmatched text
  remainingText?: string;
}

/**
 * Parse patterns from a LibreView note
 * Extracts structured data from free-form notes like:
 * - "7cp =21u + 1cr"
 * - "3.5cp + 2.5cr"
 * - "cr+5"
 * - "31g carbs"
 * - "GL 12.7"
 */
export function parseNotePatterns(note: string): ParsedNotePatterns {
  if (!note || note.trim() === "") {
    return {};
  }

  const result: ParsedNotePatterns = {};
  let remaining = note.trim();

  // Full dose breakdown pattern with units: "7cp =21u + 1cr" or "6cp=18u+2cr" or "5cp = 15u - 1cr"
  const doseBreakdownMatch = remaining.match(
    /(\d+(?:\.\d+)?)\s*cp\s*=\s*(\d+(?:\.\d+)?)\s*u(?:nits?)?\s*([+-])\s*(\d+(?:\.\d+)?)\s*(?:cr|correction)?/i,
  );
  if (doseBreakdownMatch) {
    result.doseBreakdown = {
      cp: parseFloat(doseBreakdownMatch[1]),
      baseUnits: parseFloat(doseBreakdownMatch[2]),
      correction:
        doseBreakdownMatch[3] === "+"
          ? parseFloat(doseBreakdownMatch[4])
          : -parseFloat(doseBreakdownMatch[4]),
    };
    result.cp = result.doseBreakdown.cp;
    result.correction = result.doseBreakdown.correction;
    remaining = remaining.replace(doseBreakdownMatch[0], "");
  }

  // Simple CP + correction pattern: "3.5cp + 2.5cr" or "4cp - 1cr" or "3cp+2cr"
  if (!result.doseBreakdown) {
    const cpCorrectionMatch = remaining.match(
      /(\d+(?:\.\d+)?)\s*cp\s*([+-])\s*(\d+(?:\.\d+)?)\s*(?:cr|correction)/i,
    );
    if (cpCorrectionMatch) {
      result.cp = parseFloat(cpCorrectionMatch[1]);
      result.correction =
        cpCorrectionMatch[2] === "+"
          ? parseFloat(cpCorrectionMatch[3])
          : -parseFloat(cpCorrectionMatch[3]);
      remaining = remaining.replace(cpCorrectionMatch[0], "");
    }
  }

  // Correction patterns: cr+5, cr-3, +2cr, -1cr, cr +5, correction +3
  if (result.correction === undefined) {
    const correctionMatch = remaining.match(
      /(?:cr|correction)\s*([+-])\s*(\d+(?:\.\d+)?)|([+-])\s*(\d+(?:\.\d+)?)\s*(?:cr|correction)/i,
    );
    if (correctionMatch) {
      if (correctionMatch[1]) {
        result.correction =
          correctionMatch[1] === "+"
            ? parseFloat(correctionMatch[2])
            : -parseFloat(correctionMatch[2]);
      } else if (correctionMatch[3]) {
        result.correction =
          correctionMatch[3] === "+"
            ? parseFloat(correctionMatch[4])
            : -parseFloat(correctionMatch[4]);
      }
      remaining = remaining.replace(correctionMatch[0], "");
    }
  }

  // Carb patterns: 30g, 30 grams, 30g carbs, 30 grams carbs, carbs 30g, 68gram carbs
  if (result.carbs === undefined) {
    const carbMatch = remaining.match(
      /(\d+(?:\.\d+)?)\s*g(?:rams?)?\s*(?:carbs?)?|carbs?\s*(\d+(?:\.\d+)?)\s*g?/i,
    );
    if (carbMatch) {
      result.carbs = parseFloat(carbMatch[1] || carbMatch[2]);
      remaining = remaining.replace(carbMatch[0], "");
    }
  }

  // CP patterns: 3cp, 3 cp, cp3, cp 3, 3 cp portions (only if not already found)
  if (result.cp === undefined) {
    const cpMatch = remaining.match(
      /(\d+(?:\.\d+)?)\s*cp\b|cp\s*(\d+(?:\.\d+)?)/i,
    );
    if (cpMatch) {
      result.cp = parseFloat(cpMatch[1] || cpMatch[2]);
      remaining = remaining.replace(cpMatch[0], "");
    }
  }

  // Insulin patterns: 9u, 9 units, 9units (not part of dose breakdown)
  if (result.insulin === undefined && !result.doseBreakdown) {
    const insulinMatch = remaining.match(/(\d+(?:\.\d+)?)\s*u(?:nits?)?/i);
    if (insulinMatch) {
      result.insulin = parseFloat(insulinMatch[1]);
      remaining = remaining.replace(insulinMatch[0], "");
    }
  }

  // Glucose patterns: GL12.7, gl 12.7, BG 8.5, glucose 12.7, bg12.7
  const glucoseMatch = remaining.match(
    /(?:gl|bg|glucose|blood\s*glucose)\s*(\d+(?:\.\d+)?)/i,
  );
  if (glucoseMatch) {
    result.glucose = parseFloat(glucoseMatch[1]);
    remaining = remaining.replace(glucoseMatch[0], "");
  }

  // Ketone patterns: ket 1.2, ketones 0.5, ket1.2
  const ketoneMatch = remaining.match(/(?:ket(?:ones?)?)\s*(\d+(?:\.\d+)?)/i);
  if (ketoneMatch) {
    result.ketones = parseFloat(ketoneMatch[1]);
    remaining = remaining.replace(ketoneMatch[0], "");
  }

  // Exercise pattern: "ex 5 30" or "exercise moderate 15min" or "ex light 30"
  // Intensity options:
  //   light: light/easy/small
  //   moderate: moderate/medium/med
  //   vigorous: hard/intense/vigorous
  //   numeric 1-10: 1-3=light, 4-7=moderate, 8-10=vigorous
  const exerciseMatch = remaining.match(
    /\b(?:ex|exercise)\s+(\d{1,2}|light|easy|small|moderate|medium|med|hard|intense|vigorous)(?:\s+(\d+)(?:\s*min(?:utes?)?)?)?\b/i,
  );
  if (exerciseMatch) {
    result.hasExercise = true;
    const intensityRaw = exerciseMatch[1].toLowerCase();

    // Map intensity synonyms and numeric scale to ExerciseIntensity
    let intensity: ExerciseIntensity;
    const numericIntensity = parseInt(intensityRaw, 10);
    if (!isNaN(numericIntensity)) {
      // Numeric 1-10 scale: 1-3=light, 4-7=moderate, 8-10=vigorous
      if (numericIntensity <= 3) {
        intensity = "light";
      } else if (numericIntensity <= 7) {
        intensity = "moderate";
      } else {
        intensity = "vigorous";
      }
    } else if (["light", "easy", "small"].includes(intensityRaw)) {
      intensity = "light";
    } else if (["moderate", "medium", "med"].includes(intensityRaw)) {
      intensity = "moderate";
    } else {
      // hard, intense, vigorous
      intensity = "vigorous";
    }

    result.exerciseIntensity = intensity;
    if (exerciseMatch[2]) {
      result.exerciseDuration = parseInt(exerciseMatch[2], 10);
    }
    remaining = remaining.replace(exerciseMatch[0], "");
  } else if (
    /\b(?:ex|exercise|workout|walk(?:ed|ing)?|run(?:ning)?|gym|swim(?:ming)?|cycle|cycling|bike|sport)\b/i.test(
      remaining,
    )
  ) {
    result.hasExercise = true;
  }

  // Sick mention
  if (/\b(?:sick|unwell|ill|flu|cold|fever|vomit|nausea)\b/i.test(remaining)) {
    result.hasSick = true;
  }

  // Clean up remaining text
  remaining = remaining.trim().replace(/\s+/g, " ");
  if (remaining && remaining !== note.trim()) {
    result.remainingText = remaining;
  } else if (!Object.keys(result).length) {
    result.remainingText = note.trim();
  }

  return result;
}

/**
 * Reconciliation action types
 */
export type ReconciliationAction =
  | "insert" // New entry, no match found
  | "skip" // Skip, exact match exists
  | "update" // Update existing with new values
  | "merge" // Merge fields from both
  | "review"; // Needs manual review

/**
 * Match confidence level
 */
export type MatchConfidence = "exact" | "high" | "medium" | "low" | "none";

/**
 * Reconciliation candidate - a potential LogEntry from LibreView
 */
export interface ReconciliationCandidate {
  id: string;
  timestamp: Date;
  source: "libreview";
  // Extracted/calculated values
  bloodGlucose?: number;
  carbs?: number; // grams
  cp?: number; // calculated CP
  qaBase?: number;
  qaCorrection?: number;
  biBase?: number;
  biCorrection?: number;
  ketones?: number;
  notes?: string;
  exercise?: ExerciseData; // Exercise entry from LibreView
  // Calculation metadata
  calculatedFrom?: {
    sourceCarbs?: number;
    sourceInsulin?: number;
    ratio?: number; // CP ratio used for calculation
    derivedCorrection?: number;
  };
  // Original events that contributed
  sourceEvents: LibreViewEvent[];
  // Parsed note patterns
  parsedPatterns?: ParsedNotePatterns;
}

/**
 * Match result between candidate and existing entry
 */
export interface ReconciliationMatch {
  candidate: ReconciliationCandidate;
  existingEntry?: LogEntry;
  confidence: MatchConfidence;
  timeDiff: number; // milliseconds
  // Field-level differences
  differences: {
    field: keyof LogEntry;
    candidateValue: unknown;
    existingValue: unknown;
  }[];
  suggestedAction: ReconciliationAction;
}

/**
 * Full reconciliation result
 */
export interface ReconciliationResult {
  matches: ReconciliationMatch[];
  summary: {
    total: number;
    inserts: number;
    updates: number;
    merges: number;
    skips: number;
    reviews: number;
  };
}

/**
 * Time buffer for matching entries (10 minutes for tight deduplication)
 */
export const MATCH_TIME_BUFFER_MS = 10 * 60 * 1000;

/**
 * Extended time buffer for looser matching (30 minutes)
 */
export const EXTENDED_MATCH_TIME_BUFFER_MS = 30 * 60 * 1000;

/**
 * Calculate match confidence based on time difference
 */
export function calculateMatchConfidence(timeDiffMs: number): MatchConfidence {
  const diffMinutes = Math.abs(timeDiffMs) / 60000;
  if (diffMinutes <= 2) return "exact";
  if (diffMinutes <= 5) return "high";
  if (diffMinutes <= 10) return "medium";
  if (diffMinutes <= 30) return "low";
  return "none";
}

/**
 * Extract LibreView events (insulin, carbs, notes) from parsed rows
 */
export function extractLibreViewEvents(rows: LibreViewRow[]): LibreViewEvent[] {
  const events: LibreViewEvent[] = [];

  for (const row of rows) {
    // Record type 4 = Insulin
    if (row.recordType === 4) {
      if (row.rapidActingInsulin !== undefined) {
        events.push({
          timestamp: row.deviceTimestamp,
          type: "insulin",
          rapidInsulin: row.rapidActingInsulin,
          source: "libreview",
        });
      }
      if (row.longActingInsulinValue !== undefined) {
        events.push({
          timestamp: row.deviceTimestamp,
          type: "insulin",
          longInsulin: row.longActingInsulinValue,
          source: "libreview",
        });
      }
    }

    // Record type 5 = Carbs
    if (row.recordType === 5 && row.foodCarbs !== undefined) {
      events.push({
        timestamp: row.deviceTimestamp,
        type: "carbs",
        carbs: row.foodCarbs,
        source: "libreview",
      });
    }

    // Record type 6 = Activity/Exercise
    if (row.recordType === 6) {
      // Check if it's an exercise entry ("Exercise" in Non-numeric Food column)
      if (row.nonNumericFood?.toLowerCase().includes("exercise")) {
        events.push({
          timestamp: row.deviceTimestamp,
          type: "exercise",
          source: "libreview",
        });
      } else if (row.notes) {
        // Type 6 can also have notes
        events.push({
          timestamp: row.deviceTimestamp,
          type: "notes",
          notes: row.notes,
          source: "libreview",
        });
      }
    }
  }

  return events;
}

/**
 * Group LibreView events by timestamp (within buffer)
 * Events within the time buffer are grouped together
 */
export function groupEventsByTime(
  events: LibreViewEvent[],
  bufferMs: number = 5 * 60 * 1000, // 5 minutes default for grouping
): LibreViewEvent[][] {
  if (events.length === 0) return [];

  // Sort by timestamp
  const sorted = [...events].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  const groups: LibreViewEvent[][] = [];
  let currentGroup: LibreViewEvent[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const event = sorted[i];
    const lastInGroup = currentGroup[currentGroup.length - 1];
    const timeDiff =
      event.timestamp.getTime() - lastInGroup.timestamp.getTime();

    if (timeDiff <= bufferMs) {
      currentGroup.push(event);
    } else {
      groups.push(currentGroup);
      currentGroup = [event];
    }
  }
  groups.push(currentGroup);

  return groups;
}

/**
 * Create reconciliation candidate from grouped events
 */
export function createCandidate(
  eventGroup: LibreViewEvent[],
  cgmReadings: CgmReading[],
  periodSettings?: UserSettings,
): ReconciliationCandidate {
  // Use earliest timestamp in group
  const timestamp = eventGroup.reduce(
    (earliest, e) => (e.timestamp < earliest ? e.timestamp : earliest),
    eventGroup[0].timestamp,
  );

  const candidate: ReconciliationCandidate = {
    id: `recon-${timestamp.getTime()}`,
    timestamp,
    source: "libreview",
    sourceEvents: eventGroup,
  };

  // Aggregate data from events
  let rapidInsulin: number | undefined;
  let longInsulin: number | undefined;
  let carbs: number | undefined;
  let allNotes: string[] = [];
  let hasExercise = false;

  for (const event of eventGroup) {
    if (event.rapidInsulin !== undefined) rapidInsulin = event.rapidInsulin;
    if (event.longInsulin !== undefined) longInsulin = event.longInsulin;
    if (event.carbs !== undefined) carbs = event.carbs;
    if (event.notes) allNotes.push(event.notes);
    if (event.type === "exercise") hasExercise = true;
  }

  // Parse all notes first to get exercise intensity/duration
  const notesText = allNotes.join(" ");
  const patterns = parseNotePatterns(notesText);
  candidate.parsedPatterns = patterns;

  // Set exercise data if detected (from event type or from notes)
  if (hasExercise || patterns.hasExercise) {
    candidate.exercise = {
      type: "other", // LibreView doesn't specify exercise type
      intensity: patterns.exerciseIntensity ?? "moderate", // From note or default
      duration: patterns.exerciseDuration ?? 30, // From note or default 30 mins
    };
  }

  // Set carbs (from event or parsed note)
  candidate.carbs = carbs ?? patterns.carbs;

  // Set CP (from parsed note, or calculate from carbs)
  if (patterns.cp !== undefined) {
    candidate.cp = patterns.cp;
  } else if (candidate.carbs !== undefined) {
    // Calculate CP from carbs (DAFNE: 10g = 1CP, round to 0.5)
    candidate.cp = Math.round((candidate.carbs / 10) * 2) / 2;
  }

  // Set glucose (from note patterns or nearby CGM reading)
  if (patterns.glucose !== undefined) {
    candidate.bloodGlucose = patterns.glucose;
  } else {
    // Try to find nearby CGM reading (within 10 minutes)
    const nearbyReading = cgmReadings.find(
      (r) =>
        Math.abs(r.timestamp.getTime() - timestamp.getTime()) <= 10 * 60 * 1000,
    );
    if (nearbyReading) {
      candidate.bloodGlucose = nearbyReading.glucose;
    }
  }

  // Set ketones from parsed note
  if (patterns.ketones !== undefined) {
    candidate.ketones = patterns.ketones;
  }

  // Handle insulin - calculate correction if possible
  // Priority: 1) Full breakdown from note, 2) CP+correction from note, 3) Derive from data+settings

  if (patterns.doseBreakdown) {
    // Full breakdown available: "7cp =21u + 1cr"
    candidate.qaBase = patterns.doseBreakdown.baseUnits;
    candidate.qaCorrection = patterns.doseBreakdown.correction;
    candidate.calculatedFrom = {
      sourceInsulin: rapidInsulin,
      derivedCorrection: patterns.doseBreakdown.correction,
    };
  } else if (patterns.cp !== undefined && patterns.correction !== undefined) {
    // CP + correction from note (e.g., "3.5cp + 2.5cr")
    // Calculate base units from CP using units-per-CP ratio
    const unitsPerCP = periodSettings?.cpRatios
      ? getPeriodRatioForTime(timestamp, periodSettings)
      : undefined;

    if (unitsPerCP) {
      // Base dose = CP count * units per CP ratio
      candidate.qaBase = patterns.cp * unitsPerCP;
      candidate.qaCorrection = patterns.correction;
      candidate.calculatedFrom = {
        sourceInsulin: rapidInsulin,
        ratio: unitsPerCP,
        derivedCorrection: patterns.correction,
      };
    } else {
      // No ratio - we know CP and correction but can't calculate base units
      // Use total insulin as base if available, otherwise leave it for manual review
      if (rapidInsulin !== undefined) {
        candidate.qaBase = rapidInsulin - patterns.correction;
        candidate.qaCorrection = patterns.correction;
      }
    }
  } else if (rapidInsulin !== undefined) {
    // Have insulin but need to calculate breakdown from data + settings
    if (candidate.carbs !== undefined && periodSettings?.cpRatios) {
      // Get ratio for time of day (units per CP)
      const unitsPerCP = getPeriodRatioForTime(timestamp, periodSettings);
      if (unitsPerCP) {
        // Calculate base dose: (grams / 10) * units per CP ratio
        const baseDose = (candidate.carbs / 10) * unitsPerCP;
        const correction = rapidInsulin - baseDose;

        // Only set correction if it's non-zero (threshold 0.5u)
        if (Math.abs(correction) >= 0.5) {
          candidate.qaBase = Math.round(baseDose * 2) / 2;
          candidate.qaCorrection = Math.round(correction * 2) / 2;
        } else {
          candidate.qaBase = rapidInsulin;
          candidate.qaCorrection = 0;
        }

        candidate.calculatedFrom = {
          sourceCarbs: candidate.carbs,
          sourceInsulin: rapidInsulin,
          ratio: unitsPerCP,
          derivedCorrection: candidate.qaCorrection,
        };
      } else {
        // No ratio available, just use total as base
        candidate.qaBase = rapidInsulin;
      }
    } else if (!candidate.cp && candidate.bloodGlucose !== undefined) {
      // No carbs - this might be a correction-only dose
      // Check if BG is high (>8 mmol/L suggests correction needed)
      if (candidate.bloodGlucose > 8) {
        candidate.qaBase = 0;
        candidate.qaCorrection = rapidInsulin;
        candidate.calculatedFrom = {
          sourceInsulin: rapidInsulin,
          derivedCorrection: rapidInsulin,
        };
      } else {
        // Low/normal BG with insulin but no carbs - unusual, flag for review
        candidate.qaBase = rapidInsulin;
      }
    } else {
      // No carbs/ratio/BG context, just use insulin as-is
      candidate.qaBase = rapidInsulin;
    }
  }

  // Apply correction from note if parsed but not yet applied
  if (
    patterns.correction !== undefined &&
    candidate.qaCorrection === undefined
  ) {
    candidate.qaCorrection = patterns.correction;
  }

  // Handle long-acting insulin
  if (longInsulin !== undefined) {
    candidate.biBase = longInsulin;
  }

  // Combine notes
  const noteParts: string[] = [];
  if (patterns.remainingText) {
    noteParts.push(patterns.remainingText);
  }
  if (patterns.hasExercise) {
    noteParts.push("[Exercise]");
  }
  if (patterns.hasSick) {
    noteParts.push("[Sick]");
  }
  if (noteParts.length > 0) {
    candidate.notes = noteParts.join(" ").trim();
  }

  return candidate;
}

/**
 * Get CP ratio for a given time based on period settings
 * Ratio is stored as units per CP (10g carbs).
 * Returns undefined if ratio is not configured (null)
 */
function getPeriodRatioForTime(
  timestamp: Date,
  settings: UserSettings,
): number | undefined {
  const hour = timestamp.getHours();

  // Determine period based on hour (simplified)
  // B: 5-10, L: 10-15, EM: 15-20, BT: 20-5
  let ratio: number | null | undefined;
  if (hour >= 5 && hour < 10) {
    ratio = settings.cpRatios?.B;
  } else if (hour >= 10 && hour < 15) {
    ratio = settings.cpRatios?.L;
  } else if (hour >= 15 && hour < 20) {
    ratio = settings.cpRatios?.EM;
  } else {
    ratio = settings.cpRatios?.BT;
  }

  // Convert null to undefined for consistency
  return ratio ?? undefined;
}

/**
 * Find matching entry for a candidate
 */
export function findMatchingEntry(
  candidate: ReconciliationCandidate,
  entries: LogEntry[],
  bufferMs: number = MATCH_TIME_BUFFER_MS,
): ReconciliationMatch {
  const match: ReconciliationMatch = {
    candidate,
    confidence: "none",
    timeDiff: Infinity,
    differences: [],
    suggestedAction: "insert",
  };

  // Generate sourceEventId for the candidate (to check for CGM duplicates)
  const candidateTs = getTimestampMs(candidate.timestamp);
  const candidateSourceEventId = `libreview-${candidateTs}`;

  for (const entry of entries) {
    // First, check for exact sourceEventId match (CGM-sourced duplicate)
    if (entry.sourceEventId && entry.sourceEventId === candidateSourceEventId) {
      match.existingEntry = entry;
      match.timeDiff = 0;
      match.confidence = "exact";
      match.suggestedAction = "skip"; // Exact duplicate from CGM
      return match;
    }

    const timeDiff = candidateTs - getTimestampMs(entry.timestamp);
    const absTimeDiff = Math.abs(timeDiff);

    if (absTimeDiff <= bufferMs && absTimeDiff < Math.abs(match.timeDiff)) {
      match.existingEntry = entry;
      match.timeDiff = timeDiff;
      match.confidence = calculateMatchConfidence(timeDiff);
    }
  }

  if (match.existingEntry) {
    // Calculate differences
    match.differences = calculateDifferences(candidate, match.existingEntry);

    // If existing entry is locked (CGM-sourced), don't allow updates
    if (match.existingEntry.locked) {
      // Data is similar/same within time window - treat as duplicate
      if (
        match.differences.length === 0 ||
        match.confidence === "exact" ||
        match.confidence === "high"
      ) {
        match.suggestedAction = "skip";
      } else {
        // Different data but locked - needs review (keep both?)
        match.suggestedAction = "review";
      }
    } else {
      // Determine suggested action for non-locked entries
      if (match.differences.length === 0) {
        match.suggestedAction = "skip";
      } else if (match.confidence === "exact" || match.confidence === "high") {
        // Good match with differences - suggest update
        match.suggestedAction =
          match.differences.length > 3 ? "review" : "update";
      } else {
        // Lower confidence - need review
        match.suggestedAction = "review";
      }
    }
  }

  return match;
}

/**
 * Calculate field differences between candidate and existing entry
 */
function calculateDifferences(
  candidate: ReconciliationCandidate,
  entry: LogEntry,
): ReconciliationMatch["differences"] {
  const diffs: ReconciliationMatch["differences"] = [];

  const comparableFields: (keyof LogEntry)[] = [
    "bloodGlucose",
    "carbs",
    "qaBase",
    "qaCorrection",
    "biBase",
    "biCorrection",
    "ketones",
    "notes",
  ];

  for (const field of comparableFields) {
    const candidateValue = candidate[field as keyof ReconciliationCandidate];
    const entryValue = entry[field];

    if (candidateValue !== undefined && entryValue !== candidateValue) {
      // For numbers, check if difference is significant
      if (
        typeof candidateValue === "number" &&
        typeof entryValue === "number"
      ) {
        if (Math.abs(candidateValue - entryValue) >= 0.1) {
          diffs.push({ field, candidateValue, existingValue: entryValue });
        }
      } else if (candidateValue !== entryValue) {
        diffs.push({ field, candidateValue, existingValue: entryValue });
      }
    } else if (candidateValue !== undefined && entryValue === undefined) {
      diffs.push({ field, candidateValue, existingValue: undefined });
    }
  }

  return diffs;
}

/**
 * Perform full reconciliation of LibreView events against existing entries
 */
export function reconcileLibreViewData(
  rows: LibreViewRow[],
  existingEntries: LogEntry[],
  cgmReadings: CgmReading[],
  settings?: UserSettings,
): ReconciliationResult {
  // Extract events from rows
  const events = extractLibreViewEvents(rows);

  // Group events by time
  const eventGroups = groupEventsByTime(events);

  // Create candidates from groups
  const candidates = eventGroups.map((group) =>
    createCandidate(group, cgmReadings, settings),
  );

  // Find matches
  const matches = candidates.map((candidate) =>
    findMatchingEntry(candidate, existingEntries),
  );

  // Calculate summary
  const summary = {
    total: matches.length,
    inserts: matches.filter((m) => m.suggestedAction === "insert").length,
    updates: matches.filter((m) => m.suggestedAction === "update").length,
    merges: matches.filter((m) => m.suggestedAction === "merge").length,
    skips: matches.filter((m) => m.suggestedAction === "skip").length,
    reviews: matches.filter((m) => m.suggestedAction === "review").length,
  };

  return { matches, summary };
}
